import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SmartContext } from '../../../../library/Core/SmartContext';
import SearchCriteria from './SearchCriteria';
import Results from './Results';
import type { InternalGrievanceCard, TabCounts, GrievancePushRecord } from '../services/InternalAdminGrievanceService';
import InternalAdminGrievanceService from '../services/InternalAdminGrievanceService';
import GrievanceDetailsModal from '../shared/GrievanceDetailsModal';
import PushToInstituteModal from '../shared/PushToInstituteModal';

export type ActiveTab = 'total' | 'pendingReview' | 'pushedToInstitute' | 'internalOnly';

const TAB_DEFS: { key: ActiveTab; label: string }[] = [
    { key: 'total',             label: 'Total' },
    { key: 'pendingReview',     label: 'Pending Review' },
    { key: 'pushedToInstitute', label: 'Pushed to Institute' },
    { key: 'internalOnly',      label: 'Internal Only' },
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatToday = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2,'0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

const Layout = () => {
    const { state, dispatch } = useContext(SmartContext);

    const grievances: InternalGrievanceCard[] = state.data?.grievanceList ?? [];
    const tabCounts: TabCounts = state.data?.tabCounts ?? {
        total: 0, pendingReview: 0, pushedToInstitute: 0, internalOnly: 0,
    };

    const activeTab: ActiveTab = (state.data?.searchCriteria?.tab ?? 'total') as ActiveTab;
    const isLoading = state.flags?.isDataLoading;

    // Grievance Details Modal
    const [detailGrievanceId, setDetailGrievanceId] = useState<number | null>(null);

    useEffect(() => {
        const handler = (e: Event) => {
            const { id } = (e as CustomEvent<{ id: number }>).detail;
            setDetailGrievanceId(id);
        };
        window.addEventListener('openGrievanceDetails', handler);
        return () => window.removeEventListener('openGrievanceDetails', handler);
    }, []);

    const handleStatusUpdated = (id: number, newStatus: string) => {
        const oldStatus = grievances.find((g) => g.id === id)?.status;

        // On the pendingReview tab, a grievance that leaves 'new' no longer belongs — remove it
        const leavingPendingReview = activeTab === 'pendingReview' && oldStatus === 'new' && newStatus !== 'new';
        const updated = leavingPendingReview
            ? grievances.filter((g) => g.id !== id)
            : grievances.map((g) => g.id === id ? { ...g, status: newStatus, updatedAt: formatToday() } : g);

        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'grievanceList', value: updated, errorMessages: [] } });

        // Update pendingReview tab count when status crosses the 'new' boundary
        if (oldStatus !== newStatus) {
            const prev = state.data?.tabCounts ?? {};
            let pendingReview = prev.pendingReview ?? 0;
            if (oldStatus === 'new' && newStatus !== 'new') pendingReview = Math.max(0, pendingReview - 1);
            if (oldStatus !== 'new' && newStatus === 'new') pendingReview += 1;
            if (pendingReview !== prev.pendingReview) {
                dispatch({
                    type: 'CONTROL_VALUE_CHANGE',
                    payload: { dataKey: 'tabCounts', value: { ...prev, pendingReview }, errorMessages: [] },
                });
            }
        }
    };

    // Push to Institute Modal
    const [pushModal, setPushModal] = useState<{
        mode: 'create' | 'edit';
        grievance: InternalGrievanceCard;
    } | null>(null);

    useEffect(() => {
        const handler = (e: Event) => {
            const { mode, grievance } = (e as CustomEvent<{
                mode: 'create' | 'edit';
                grievance: InternalGrievanceCard;
            }>).detail;
            setPushModal({ mode, grievance });
        };
        window.addEventListener('openPushToInstitute', handler);
        return () => window.removeEventListener('openPushToInstitute', handler);
    }, []);

    const handlePushSuccess = (grievanceId: number, push: GrievancePushRecord) => {
        const isCreate = !grievances.find((g) => g.id === grievanceId)?.push;
        const updated  = grievances.map((g) => g.id === grievanceId ? { ...g, push } : g);
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'grievanceList', value: updated, errorMessages: [] } });
        if (isCreate) {
            const prev = tabCounts;
            dispatch({
                type: 'CONTROL_VALUE_CHANGE',
                payload: {
                    dataKey: 'tabCounts',
                    value: { ...prev, pushedToInstitute: (prev.pushedToInstitute ?? 0) + 1 },
                    errorMessages: [],
                },
            });
        }
    };

    const handleTabChange = async (tab: ActiveTab) => {
        const newCriteria = { ...state.data.searchCriteria, tab };
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'searchCriteria', value: newCriteria, errorMessages: [] } });
        const response = await InternalAdminGrievanceService.search(newCriteria);
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'grievanceList', value: response.grievances ?? [], errorMessages: [] } });
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'tabCounts', value: response.tabCounts ?? tabCounts, errorMessages: [] } });
    };

    const handleExport  = () => InternalAdminGrievanceService.exportCSV(grievances);
    const handleAddNew  = () => {
        // redirect to support/add-grievance
        window.location.href = '/support/add-grievance';
    }

    const pushedCount = tabCounts.pushedToInstitute;

    return (
        <div className="main flex-1">
            <section className="alumni_directory_details container-fluid">
                <div className="max-1250 pt-3 pt-sm-5 px-lg-0 pb-5 overflow-hidden mx-auto">
                    <div className="alumni_statistics">

                        {/* ── Page header ─────────────────────────────────── */}
                        <div className="row justify-content-between align-items-center mb-4 g-2">
                            <div className="col">
                                <h2 className="font-26 font-600 mb-0">Grievance Management</h2>
                                <p className="font-18 mb-0">Internal view — all captured alumni grievances across all institutes</p>
                            </div>
                            {/* Pushed to Institute counter */}
                            <div className="col-auto">
                                <a
                                    href="javascript:;"
                                    className="btn-border-gray w-auto height-40 d-inline-flex align-items-center font-14 text-decoration-none px-3"
                                >
                                    <i className="bi bi-check-all font-22 position-relative me-1"></i>
                                    <span>{pushedCount} Pushed to Institute</span>
                                </a>
                            </div>
                            {/* Add New Grievance */}
                            <div className="col-auto">
                                <Link to="/support/add-grievance" className="btn btn-green w-auto height-40 d-inline-flex text-white font-14 text-decoration-none px-3"
                                >
                                    <i className="icon-plus circle"></i>
                                    <span>Add New Grievance</span>
                                </Link>
                            </div>
                            {/* Export */}
                            <div className="col-auto">
                                <button
                                    onClick={handleExport}
                                    className="btn btn-skyblue w-auto height-40 d-inline-flex text-white font-14 text-decoration-none px-3"
                                >
                                    <i className="bi bi-box-arrow-up-right font-16 me-1"></i>
                                    <span>Export</span>
                                </button>
                            </div>
                        </div>

                        <div className="ActiveListings_present">

                            {/* ── Filters ─────────────────────────────────── */}
                            <SearchCriteria />

                            {/* ── Tab bar ─────────────────────────────────── */}
                            <div className="nav nav-pills gap-2 mb-4 border-tab" role="tablist">
                                {TAB_DEFS.map(({ key, label }) => {
                                    const count   = tabCounts[key as keyof TabCounts];
                                    const isTotal = key === 'total';
                                    return (
                                        <button
                                            key={key}
                                            className={`nav-link rounded-pill d-flex align-items-center gap-2 ${activeTab === key ? 'active' : ''}`}
                                            onClick={() => handleTabChange(key)}
                                            type="button"
                                            role="tab"
                                            aria-selected={activeTab === key}
                                        >
                                            <span className={isTotal ? 'fw-bold' : ''}>{label}</span>
                                            <span className="fw-medium font-12 bg-white bg-opacity-50 px-2 rounded-pill">
                                                {count}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* ── Results count ────────────────────────────── */}
                            <div className="d-flex flex-wrap align-items-center mb-2">
                                <h3 className="font-16 font-400 mb-0 me-2 text-secondary">
                                    Showing{' '}
                                    <span className="text-dark font-500">{grievances.length} grievances</span>
                                    {' '}<span className="font-20 font-500">·</span>{' '}
                                    <span className="text-green font-500">{pushedCount} pushed to institute</span>
                                </h3>
                            </div>

                            {/* ── Table area ──────────────────────────────── */}
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading…</span>
                                    </div>
                                </div>
                            ) : (
                                <Results />
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Grievance Details Modal */}
            <GrievanceDetailsModal
                grievanceId={detailGrievanceId}
                onClose={() => setDetailGrievanceId(null)}
                onStatusUpdated={handleStatusUpdated}
                showBothPriorities
            />

            {/* Push to Institute Modal */}
            {pushModal && (
                <PushToInstituteModal
                    mode={pushModal.mode}
                    grievance={pushModal.grievance}
                    onClose={() => setPushModal(null)}
                    onSuccess={handlePushSuccess}
                />
            )}
        </div>
    );
};

export default Layout;
